import axios from 'axios';

// 统一 API 基址：强制走 Workers，避免 GET 与 POST 命中不同后端导致数据不同步
const DEFAULT_WORKERS_API = 'https://api.tkerp.ruohaotech.com/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || DEFAULT_WORKERS_API;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// 账号相关API
export const getAccounts = (params = {}) => {
  const p = { ...params, _ts: Date.now() };
  return api.get('/accounts', { params: p });
};

export const getAccount = (id) => {
  return api.get(`/accounts/${id}`, { params: { _ts: Date.now() } });
};

export const createAccount = (data) => {
  return api.post('/accounts', data);
};

export const updateAccount = (id, data) => {
  return api.put(`/accounts/${id}`, data);
};

export const deleteAccount = (id) => {
  return api.delete(`/accounts/${id}`);
};

export const batchUpdateAccountStatus = (ids, status, options = {}) => {
  // 后端已兼容 PUT/POST，这里统一使用 POST 以规避某些环境对 PUT+body 的限制
  const payload = { ids, status };
  if (options.transfer) payload.transfer = true;
  return api.post('/accounts/batch/status', payload);
};

export const batchDeleteAccounts = (ids) => {
  return api.post('/accounts/batch/delete', { ids });
};

// 获取单个账号的全部图片
export const getAccountImages = (id) => {
  return api.get(`/accounts/${id}/images`);
};

// 销售相关API
export const getSales = (params = {}) => {
  return api.get('/sales', { params });
};

export const createSale = (data) => {
  return api.post('/sales', data);
};

export const updateSale = (id, data) => {
  return api.put(`/sales/${id}`, data);
};

export const deleteSale = (id) => {
  return api.delete(`/sales/${id}`);
};

// 统计数据API
export const getStats = () => {
  return api.get('/stats');
};

// 获取所有分类
export const getCategories = () => {
  return api.get('/categories');
};

// 导入导出API
export const exportAccounts = () => {
  return api.get('/export/accounts', { responseType: 'blob' });
};

export const importAccounts = (payload, options = {}) => {
  const defaultStatus = options.defaultStatus || '';
  if (typeof FormData !== 'undefined' && payload instanceof FormData) {
    if (defaultStatus) payload.append('defaultStatus', defaultStatus);
    return api.post('/import/accounts', payload, {
      params: defaultStatus ? { defaultStatus } : undefined
    });
  }
  if (defaultStatus) {
    return api.post('/import/accounts', {
      rows: Array.isArray(payload) ? payload : [],
      defaultStatus
    });
  }
  return api.post('/import/accounts', payload);
};

// 上传接口
export const uploadAccountImage = (formData) => {
  // 注意：不要手动设置 Content-Type，让浏览器自动设置（包含 boundary）
  return api.post('/upload/account-image', formData);
};

export const uploadAccountImages = (formData) => {
  // 注意：不要手动设置 Content-Type，让浏览器自动设置（包含 boundary）
  return api.post('/upload/account-images', formData);
};

// 认证接口
export const login = (username, password) => api.post('/login', { username, password });
export const logout = () => api.post('/logout');
export const getMe = () => api.get('/me');
export const getAuthSettings = () => api.get('/settings/auth');
export const updateAuthSettings = (payload) => api.put('/settings/auth', payload);
export const getTwoFaSettings = () => api.get('/settings/two-fa');
export const updateTwoFaSettings = (payload) => api.put('/settings/two-fa', payload);

// 账号类型管理接口
export const getAccountTypes = () => api.get('/account-types');
export const createAccountType = (data) => api.post('/account-types', data);
export const updateAccountType = (id, data) => api.put(`/account-types/${id}`, data);
export const deleteAccountType = (id) => api.delete(`/account-types/${id}`);

// 邮箱系统API（通过后端代理，安全）
export const getEmailList = async () => api.get('/email/list');
export const createEmailAccount = async (local, domain = 'ruohaotech.com') =>
  api.post('/email/create', { local, domain });
export const deleteEmailAccount = async (slug) => api.post('/email/delete', { slug });
export const createEmailToken = async (addr) => api.post('/email/token', { addr });
export const getEmailInbox = (slug, limit = 30) =>
  api.get('/email/inbox', { params: { slug, limit } });
export const getEmailMail = (slug, id) =>
  api.get('/email/mail', { params: { slug, id } });
export const getEmailCode = (slug) =>
  api.get('/email/code', { params: { slug } });
export const getEmailStats = () => api.get('/email/stats');
export const getEmailAdminMails = (params = {}) =>
  api.get('/email/admin-mails', { params });
export const getEmailAdminMail = (address, id) =>
  api.get('/email/admin-mail', { params: { address, id } });

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 如果是FormData，删除Content-Type让浏览器自动设置（包含boundary）
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // 添加认证token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器（合并：401处理 + 数据解包 + 统一错误消息）
api.interceptors.response.use(
  async (response) => {
    // 如果误打到了静态 Pages，返回的 content-type 会是 text/html，这里自动切换到 Workers 并重试一次
    try {
      const ct = (response && response.headers && (response.headers['content-type'] || response.headers.get?.('content-type'))) || '';
      if (ct.includes('text/html') && !response.config?._retried) {
        const newConfig = { ...response.config, baseURL: DEFAULT_WORKERS_API, _retried: true };
        return await api.request(newConfig);
      }
    } catch (_) {}
    return response.data;
  },
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem('token');
      if (typeof window !== 'undefined') window.location.reload();
      return Promise.reject(error);
    }
    // 405（Method Not Allowed）多见于把 POST 发到了 Pages 静态站，这里兜底切到 Workers 并重试一次
    if (error?.response?.status === 405 && error?.config && !error.config._retried) {
      const newConfig = { ...error.config, baseURL: DEFAULT_WORKERS_API, _retried: true };
      return api.request(newConfig);
    }
    if (error && error.response) {
      const serverMsg = error.response.data?.error || error.response.data?.message;
      return Promise.reject(new Error(serverMsg || '请求失败'));
    }
    if (error && error.request) {
      return Promise.reject(new Error('网络连接失败，请检查网络设置'));
    }
    return Promise.reject(new Error(error?.message || '请求失败'));
  }
);

export default api;
